// The English catalogue. Asserted against `de.js` in both directions, so
// neither file can grow a key the other lacks.
//
// Not a literal translation where a literal one would be wrong: "Merken" is
// "Save" rather than "Remember", and the bucket labels are the words an
// English speaker uses for those ranges rather than the German ones rendered
// word by word.

// A date written out, not "2026-09-15": a date read aloud digit by digit is
// the kind of thing that makes a screen reader user give up on a calendar.
// Through Intl, so the two languages differ where they should — "12. August
// 2026" against "12 August 2026" — without either spelling out month names.
const LONG_DATE = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
});

function longDate(iso) {
  const date = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? String(iso) : LONG_DATE.format(date);
}

const LIST = new Intl.ListFormat('en-GB', { style: 'long', type: 'conjunction' });

import legal from './legal-en.js';
import mail from './mail-en.js';

export default {
  // Whole documents rather than chrome, so the prose lives in its own file —
  // spread in flat, so the key-parity test covers it without knowing that.
  ...legal,
  // Everything this product sends. Beside the catalogue for the same reason
  // the legal pages are: prose rather than chrome, spread in flat so the
  // key-parity test covers it without knowing it is special.
  ...mail,

  // chrome
  brandMark: 'tba',
  brandName: 'tips',
  navExplore: 'Explore',
  navFeed: 'Following',
  navSaved: 'Saved',
  panelTitle: 'Panel',
  navDates: 'Dates',
  navPage: 'Page',
  navAccountSettings: 'Account',
  navUsers: 'Users',
  navStats: 'Stats',
  navCrawl: 'Crawl',
  backLabel: 'Back',
  // The quiet shortcut on the public event page. It has no visible word — the
  // label is its whole accessible name and its tooltip.
  editInPanel: 'Edit in the panel',
  navBackend: 'Panel',
  navAccount: 'Account',
  navSignIn: 'Sign in',
  themeToggle: 'Switch theme',
  switchLanguage: 'Deutsch',
  switchLanguageLabel: 'Auf Deutsch wechseln',

  navMyPage: 'Page',

  // profile
  profileTitle: 'Public profile',
  profileLede: 'This is what visitors see on your page. None of it is required, and the page stays unpublished until you say so below.',
  fieldNickname: 'Short name',
  nicknameHint: 'Your address becomes tba.tips/u/shortname. Lowercase letters, digits and hyphens, at least three characters.',
  fieldDisplayName: 'Display name',
  fieldAbout: 'About you',
  fieldContact: 'Contact',
  contactHint: 'Shown publicly on your page. Not your sign-in address — that stays private.',
  fieldInstagram: 'Instagram',
  fieldSoundcloud: 'SoundCloud',
  fieldBandcamp: 'Bandcamp',
  fieldSocials: 'Social',
  publicPageTitle: 'Your page',
  publicPageLede: 'Your page is not published until you say so here. It shows what you are doing — and that is your call, not ours.',
  pagePublish: 'Publish page',
  pageUnpublish: 'Take the page offline',
  pageNeedsNickname: 'Set a short name above and your page gets an address.',
  nickname_format: 'A short name may only contain lowercase letters, digits and single hyphens.',
  nickname_too_short: 'That short name is too short.',
  nickname_too_long: 'That short name is too long.',
  nickname_taken: 'That short name is already taken.',
  nickname_reserved: 'That short name is not available.',

  // Just the word — see the note in de.js.
  footerAbout: 'About',
  dayEventCount: (n) => (n === 1 ? '1 event' : `${n} events`),
  footerLegal: 'Legal Notice',
  footerReport: 'Report',
  footerData: 'Data: ODbL',

  // filters
  search: 'Search',
  filterApply: 'Filter',
  filterReset: 'Reset',
  // The name of the whole band, which is what the two handles are grouped
  // under. Two sliders with no group label are two anonymous numbers.
  filterTime: 'Date range',
  // The two ends of the range, both inclusive months, and the one date
  // question no range expresses.
  filterFrom: 'From',
  filterTo: 'Until',
  filterWeekends: 'Weekends',
  filterToday: 'Today',
  // The selection written out is no longer a key — see the note in de.js and
  // formatDateRange in server/lib/i18n.js. Intl punctuates a span per language,
  // so there is nothing left here for a template to get wrong.
  filterCity: 'City',
  filterTag: 'Tag',
  filterGenre: 'Genre',

  // The facet vocabulary. Facets are chrome we curate, so they are translated;
  // tags are what publishers write, so they are not. `taxonomy.name_key` points
  // at these keys, and a key that is missing here falls back to itself — a
  // moderator who adds a facet from the dashboard cannot also ship a locale.

  // The fourth domain, for everything the first three do not describe. No
  // styles under it: a flea market and a panel share no axis a style could
  // name, and inventing one would be a vocabulary nobody could fill in.
  facetOther: 'Other',
  facetTalk: 'Talk',
  facetWorkshop: 'Workshop',
  facetPanel: 'Panel',
  facetReading: 'Reading',
  facetScreening: 'Screening',
  facetConference: 'Conference',
  facetFleaMarket: 'Flea market',
  facetDemo: 'Demo',
  facetActivity: 'Activity',

  facetMusic: 'Music',
  facetArt: 'Art',
  facetFood: 'Food',
  facetConcert: 'Concert',
  facetDj: 'DJ',
  facetClubbing: 'Clubbing',
  facetParty: 'Party',
  facetRave: 'Rave',
  facetLive: 'Live',
  facetListening: 'Listening',
  facetExhibition: 'Exhibition',
  facetVernissage: 'Vernissage',
  facetFinissage: 'Finissage',
  facetStreetfood: 'Street food',
  facetDrinks: 'Drinks',
  facetRestaurant: 'Restaurant',
  facetPopup: 'Pop-up',
  facetSupperClub: 'Supper Club',
  facetTechno: 'Techno',
  facetHouse: 'House',
  facetDisco: 'Disco',
  facetAmbient: 'Ambient',
  facetDrumAndBass: 'Drum & Bass',
  facetDub: 'Dub',
  facetJazz: 'Jazz',
  facetPunk: 'Punk',
  facetIndie: 'Indie',
  facetHiphop: 'HipHop',
  facetExperimental: 'Experimental',
  facetClassical: 'Classical',
  facetElectro: 'Electro',
  facetTrance: 'Trance',
  facetPop: 'Pop',
  facetPerformance: 'Performance',
  facetPainting: 'Painting',
  facetSculpture: 'Sculpture',
  facetFilm: 'Film',
  facetPhotography: 'Photography',
  facetInstallation: 'Installation',
  facetDrawing: 'Drawing',
  facetPrint: 'Printmaking',
  facetMediaArt: 'Media art',
  facetSoundArt: 'Sound art',
  facetGuidedTour: 'Guided tour',
  facetOpenStudio: 'Open studio',
  facetAsian: 'Asian',
  facetMediterranean: 'Mediterranean',
  facetFusion: 'Fusion',
  facetDeftig: 'Hearty',
  facetVegan: 'Vegan',
  facetIndoor: 'Indoor',
  facetOutdoor: 'Outdoor',

  allDates: 'Any date',
  allCities: 'All cities',
  allTags: 'All tags',
  filterFree: 'Free',
  filterAnySetting: 'Anywhere',
  // The cycling control's whole accessible name: where it stands, then what a
  // click does. The visible label is only the first half, because the second
  // half is what the arrow shape is for.
  settingCycle: (current, next) => `Indoor or outdoor: ${current}. Next: ${next}`,
  fieldPriceFree: 'Free entry',

  // The moderator's vocabulary screen. "The rows are data, not an enum" was
  // true of the schema from the first migration; this is where it becomes true
  // of the product.
  vocabulary: 'Vocabulary',
  vocabularyHint: 'Domains, formats, styles and the setting. A new entry is in the editor and the filter at once — there is no deploy in between.',
  vocabularyDelete: 'Delete',
  vocabularyAdd: 'Add',
  vocabularyName: 'Name',
  vocabularySlug: 'Short form (optional)',
  vocabularyKind: 'Kind',
  vocabularyParent: 'Domain',
  vocabularyUsed: count => count === 1 ? '1 series' : `${count} series`,
  vocabularyDeleteBlocked: 'In use — move those events first, then delete.',
  kindDomain: 'Domain',
  kindFormat: 'Format',
  kindStyle: 'Style',
  kindSetting: 'Setting',

  allDomains: 'All domains',
  allFormats: 'All formats',
  allStyles: 'All styles',
  allSettings: 'Indoor and outdoor',
  allGenres: 'All genres',
  timeToday: 'Today',
  timeTomorrow: 'Tomorrow',
  timeWeekend: 'Weekend',
  timeNext7: 'Next 7 days',

  jsonLdFeedList: 'Events in the Rhineland',
  jsonLdCityList: (city) => `Events in ${city}`,
  jsonLdVenueList: (name) => `Programme of ${name}`,

  viewLabel: 'View',
  viewList: 'List',
  viewCalendar: 'Calendar',
  viewMap: 'Map',
  previousMonth: 'Previous month',
  nextMonth: 'Next month',

  // Map. The credit is an ODbL condition rather than a courtesy — it stands on
  // every page that draws an outline.
  mapAttribution: 'Map data © OpenStreetMap contributors and Protomaps, ODbL',
  mapEmpty: 'Nothing with an address is listed for this day.',
  mapDayLabel: 'Pick a day',
  mapDayPrev: 'Previous day',
  mapDayNext: 'Next day',
  mapCityLabel: (city) => `Street map of ${city}`,
  mapRegionLabel: 'Overview map of the whole region',
  mapCityLink: (city) => `Look at ${city} more closely`,
  mapWholeRegion: 'Whole region',
  mapZoomIn: 'Closer in',
  mapZoomOut: 'Further out',
  mapZoomReset: 'Reset the view',

  // buckets
  bucketToday: 'Today',
  bucketTomorrow: 'Tomorrow',
  bucketWeekend: 'This weekend',
  bucketWeek: 'This week',
  bucketLater: 'Later',
  bucketPast: 'Past',

  // cards
  bookmark: 'Save',
  bookmarked: 'Saved',
  saveCount: (count) => `${count}`,
  follow: 'Follow',
  following: 'Following',
  cancelledNotice: 'This date is cancelled.',
  tickets: 'Tickets',
  moreLinks: 'More links',
  postedBy: 'Posted by',
  address: 'Address',
  price: 'Entry',
  subscribeCalendar: 'Subscribe to calendar',

  // Share sheet and lightbox (M5). The full sheet renders on the event detail
  // page only; in lists the button is nothing but a link to this anchor.
  share: 'Share',
  shareSheetTitle: 'Share & add to calendar',
  shareEvent: (title) => `Share “${title}”`,
  addToCalendar: 'Add to calendar',
  calendarGoogle: 'Google Calendar',
  calendarOutlook: 'Outlook',
  subscribeVenue: (name) => `Subscribe to every date from ${name}`,
  sendTo: 'Send to someone',
  shareWhatsapp: 'WhatsApp',
  shareTelegram: 'Telegram',
  shareEmail: 'E-mail',
  shareNative: 'Share …',
  enlargeImage: 'Enlarge image',

  botMaintained: 'This programme is maintained by tipsBOT from public sources — not by the institution itself.',
  botMaintainedClaim: 'Is this yours? Take over the page.',

  unclaimedPage: 'Nobody from the institution has taken over this page yet.',

  // empty states
  emptyFiltered: 'Nothing listed for that.',
  emptyFilteredAction: 'Clear filters',
  // Names the *range* as the empty thing, not the filters: the city and the
  // genre beside it are still perfectly good. The link does the widening,
  // because "drag a handle back" is an instruction rather than a way out.
  emptyRange: 'Nothing listed in that stretch.',
  emptyRangeAction: 'Show the whole year',
  emptyFeed: 'Nothing listed yet. If you run an institution:',
  emptyFeedAction: 'add your programme',
  emptyVenue: 'No dates listed at the moment.',

  // /meine
  feedTitle: 'Following',
  feedEmpty: 'You are not following anybody yet. Every page has a follow button — after that, this is their programme and nothing else.',
  feedQuiet: 'Nobody you follow has announced anything.',

  // /gemerkt
  subscribeSaved: 'Subscribe to your list',
  subscribeSavedHint: 'A calendar subscription that picks up every new entry by itself. The address only comes into being when you ask for one.',
  subscribeSavedLive: 'A subscription address exists. Reissuing kills the old one immediately.',
  subscribeCreate: 'Create a subscription address',
  subscribeRotate: 'Reissue',
  subscribeRevoke: 'End the subscription',
  subscribeOpen: 'Open in a calendar',
  subscribeOnce: 'You see this address only now. It is not stored — lose it and you make a new one.',
  subscribeWarning: 'Anyone holding this address can see which nights you intend to be at. Do not pass it on, and reissue it if that happens anyway.',
  backToSaved: 'Back to your list',
  archiveTitle: 'Archive',
  archiveLede: 'Everything that already happened. Most recent first.',
  archiveEmpty: 'Nothing past matches this selection.',
  pagerNewer: 'Newer',
  pagerOlder: 'Older',
  pagerMore: 'More dates',
  pagerBack: 'Previous dates',
  pagerAdded: (count) => (count === 1
    ? 'One more date loaded.'
    : `${count} more dates loaded.`),
  emptyPage: 'That is the end of the list.',
  emptyPageAction: 'Back to the start',
  savedTitle: 'Saved',
  savedEmpty: 'You have not saved anything yet. Every date has a save button — after that, it is here.',
  savedAnonymous: 'This list lives in this browser, not in an account — it disappears if you clear site data or switch devices.',
  savedAnonymousAction: 'Add an e-mail address',
  savedAnonymousAfter: ' and it stays.',
  savedCancelled: (count) => (count === 1
    ? 'One date you saved has been cancelled.'
    : `${count} dates you saved have been cancelled.`),
  savedRetention: 'This is as far back as it goes: saved dates are deleted after twelve months. What remains is the count on the date itself.',

  // publisher application
  applyTitle: 'Take over this page',
  applyLede: 'Creating an institution needs nobody. What is checked by hand is whether you really belong to this one — after that its page moves to the short address.',
  applyVenueName: 'Name of the institution',
  applyCity: 'City',
  applyAddress: 'Address',
  applyWebsite: 'Website or Instagram',
  applyContact: 'How do we reach you?',
  applyNote: 'What do you do?',
  applySubmit: 'Send application',
  applyNameMissing: 'What is the institution called?',
  applyPendingTitle: 'Application pending',
  applyPending: (venue) => `Your application for ${venue} is with us. We will get back to you once somebody has looked at it.`,

  claimTitle: 'Take over this page',
  claimLede: (venue) => `You are asking to take over ${venue}. We check by hand that you really belong there — after that the page is yours and you can design it.`,
  claimKeepsProgramme: 'Everything already listed stays. Whoever has been maintaining it keeps editor access until you change that.',
  backToCalendar: 'Back to the calendar',

  // account
  accountTitle: 'My account',
  tabProfile: 'Profile',
  tabPage: 'Page',
  tabAccount: 'Account',
  tabData: 'Data',
  fieldAvatar: 'Avatar',
  fieldRole: 'Role',
  fieldVenues: 'Institutions',
  roleGuest: 'Guest',
  rolePerson: 'Person',
  roleInstitution: 'Institution',
  roleModerator: 'Moderator',
  roleAdmin: 'Admin',
  signOut: 'Sign out',
  signOutHint: 'Signs you out on this device. The session is revoked, not just the cookie cleared.',
  signOutEverywhere: 'Sign out everywhere',
  signOutEverywhereHint: 'Ends every session on every device, including this one. Use it if you have lost a device.',
  yourDataTitle: 'Your data',
  yourData: 'We store your e-mail address, optionally a name, what you saved and who you follow. No real name, no address, no location data, no advertising identifiers.',
  downloadAll: 'Download everything',
  digestTitle: 'Weekly e-mail',
  digestLede: 'Once a week, what the institutions you follow have announced. Only if you want it, and every mail carries an unsubscribe link.',
  digestSubscribe: 'Get the weekly e-mail',
  digestUnsubscribe: 'Stop the weekly e-mail',
  deleteTitle: 'Delete account',
  deleteLede: 'Deleting removes your e-mail address, what you saved and who you follow, immediately and permanently. They disappear from the backups within seven days.',
  deleteMemberWarning: (count) => (count === 1
    ? 'Note: you maintain one institution. Its programme stays; your membership does not.'
    : `Note: you maintain ${count} institutions. Their programmes stay; your membership does not.`),
  deleteConfirmWord: 'DELETE',
  deleteConfirmLabel: 'Type DELETE to confirm',
  deleteSubmit: 'Delete this account for good',

  passkeyTitle: 'Security key',
  passkeyLede: 'A key is required for moderation and administration. It is added to the code, never instead of it — a lost phone must not be a lost account.',
  passkeyAdd: 'Add a key',
  passkeyLabel: 'Label (so you recognise it later)',
  passkeyNone: 'No key registered yet.',
  passkeyAdded: (when) => `added on ${when}`,
  passkeyLastUsed: (when) => `last used on ${when}`,
  passkeyNeverUsed: 'not used yet',
  passkeyRemove: 'Remove',
  passkeyRemoveConfirm: 'Really remove this key?',
  passkeyUnsupported: 'This browser cannot do security keys. Use another one, or another device.',
  passkeyFailed: 'That did not work. Please try again.',
  passkeyStaleCode: 'A key can only be registered right after signing in. Sign out and back in with a new code, then this will work.',
  passkeyEnrolling: 'You need a security key before you can go on. Moderation and administration stay closed until one is registered.',
  passkeyStepTitle: 'Confirm with your key',
  passkeyStepLede: 'The code was right. Now your security key — only then does a session exist.',
  passkeyStepButton: 'Sign in with a key',
  passkeyStepExpired: 'That took too long. Please start again.',

  // login
  signInTitle: 'Sign in',
  signInLede: 'We will send you a six-digit code.',
  fieldEmail: 'E-mail',
  requestCode: 'Send code',
  codeTitle: 'Enter the code',
  codeLede: (minutes) => `The code is valid for ${minutes} minutes, and only in this window.`,
  fieldCode: 'Code',
  trustDevice: 'Stay signed in on this device for a year (otherwise 90 days)',
  resendCode: 'Send a new code',
  honeypotLabel: 'Website',

  devLoginTitle: 'Local only',
  devLoginNote: (logFile) => `Otherwise the code goes to the server console and to ${logFile}. This list only appears while MAIL_DRIVER=log is set and the build is not a production build.`,

  errInvalidEmail: 'That address does not look right.',
  errWrongCode: 'That code is not correct.',
  errExpired: 'The code has expired. Request a new one.',
  errLocked: 'Too many attempts. Request a new code.',
  errWrongBrowser: 'That code belongs to another window. Request a new one here.',
  errRateLimited: 'Too many requests. Try again in a few minutes.',
  errBanned: 'This account is suspended.',
  errGeneric: 'That did not work.',
  // ------------------------------------------------------------- backend
  //
  // Paired key for key with the German block, which the parity test enforces.

  backend: 'Backend',
  venuesEmptyTitle: 'You do not maintain an institution yet.',
  venuesEmptyHint: 'Create one — a name and a city are enough. Everything else can follow later.',
  newInstitution: 'New institution',
  newInstitutionHint: 'Your page starts at tba.tips/i/name. Once we have confirmed you belong there, it moves to the short address.',
  institutionNameRequired: 'It needs a name.',
  createFailed: 'Could not create it.',
  series: 'Events',
  seriesEmpty: 'Nothing added yet.',
  owner: 'Owner',
  editor: 'Editor',
  published: 'public',
  draft: 'draft',
  loading: 'Loading …',
  notFound: 'This page does not exist in the backend.',
  failed: 'That did not work.',
  signedOut: 'Not signed in.',
  toWebsite: 'To the website',
  switchTheme: 'Switch theme',

  calendar: 'Calendar',
  toToday: 'Today',
  noEventsThisMonth: 'Nothing listed this month.',
  cancelledShort: 'cancelled',
  movedShort: 'moved',

  edit: 'Edit',
  move: 'Move',
  cancelEvent: 'Cancel',
  reactivate: 'Reinstate',
  pickTarget: 'Pick the target day — Tab moves, Enter sets, Esc cancels',
  moveTo: (date) => `Move to ${longDate(date)}`,
  moveFailed: 'Moving did not work.',
  moveConflict: 'Somebody else changed this date in the meantime.',
  moveOccupied: 'The series already runs on that day.',
  cancelFailed: 'That did not work.',
  confirmPast: 'That day is in the past. Move it anyway?',

  newOccurrence: 'New date',
  fieldTitle: 'Title',
  fieldStartDate: 'Date',
  fieldStartTime: 'Starts',
  fieldEndDate: 'Last day',
  fieldMultiDay: 'Runs over several days',
  fieldNone: '—',
  save: 'Save',
  cancelAction: 'Cancel',
  titlePrompt: 'Start with a title.',
  endBeforeStart: 'The last day is before the start.',
  saveFailed: 'Saving did not work.',
  createdDraft: 'Created — as a draft. It goes public when you publish it.',

  recurrence: 'Repeats',
  repeats: 'Happens',
  freqOnce: 'once',
  freqWeekly: 'weekly',
  freqMonthly: 'monthly',
  everyWeek: 'every week',
  every2Weeks: 'every 2nd week',
  every3Weeks: 'every 3rd week',
  every4Weeks: 'every 4th week',
  onWeekday: 'on',
  byWeekday: 'on a weekday',
  byDate: 'on a date',
  first: 'first',
  second: 'second',
  third: 'third',
  fourth: 'fourth',
  last: 'last',
  untilLabel: 'until',
  nextDates: 'Next dates',
  noDates: 'This rule produces no dates.',

  fieldSubtitle: 'Subtitle',
  fieldDescription: 'Description',
  fieldDuration: 'Duration in minutes',
  fieldPrice: 'Entry',

  // The facet editor. The style label changes with the domain — Genre under
  // Music, Medium under Art, Küche under Food — because "genre" is the wrong
  // word outside music and a single label would be wrong twice out of three.
  fieldFacets: 'Classification',
  fieldDomain: 'Domain',
  fieldFormat: 'Format',
  fieldStyle: 'Style',
  fieldSetting: 'Indoor or outdoor',
  facetStyleMusic: 'Genre',
  facetStyleArt: 'Medium',
  facetStyleFood: 'Cuisine',
  facetHint: 'Pick a domain first — formats and styles appear then. All optional, as long as one tag or one facet is set.',
  classificationRequired: 'Set at least one tag or one facet, or the event is findable only by its date.',

  fieldTicketUrl: 'Ticket link',
  fieldLinks: 'More links',
  fieldLinksDate: 'Links for this date only',
  linkLabel: 'Label',
  linkUrl: 'Link address',
  addLink: 'Add link',
  removeLink: 'Remove link',
  linkHint: 'http(s) only. Without a label we show the domain. Six per date at most.',
  linkBadUrl: 'That is not a valid http(s) address.',
  linkTooMany: 'Six links per date at most — series and date together.',
  editEvent: 'Edit event',
  newEvent: 'New event',
  publish: 'Publish',
  unpublish: 'Unpublish',
  saved: 'Saved.',
  publishedNow: 'Public now.',
  unpublishedNow: 'A draft again, and no longer public.',

  editVenue: 'Edit institution',
  fieldVenueName: 'Name',
  fieldCity: 'City',
  fieldAddress: 'Address',
  fieldWebsite: 'Website',
  fieldProfileImage: 'Profile picture',
  fieldSlug: 'Short URL',
  slugWarning: 'The short URL is in your Instagram bio. Old addresses redirect permanently, but only change it if you have to.',
  ownerOnly: 'Only the owner changes the profile, the short URL, the calendar page and the team. Maintaining and exporting the programme is yours.',
  confirmRename: 'Really change the short URL? The old one redirects, but printed links and posters will be wrong.',
  slugRejected: 'That short URL will not work — it is taken, reserved, or too short.',
  venueNameRequired: 'The institution needs a name.',

  bulkActions: 'Actions',
  pauseSeries: 'Pause',
  pauseFrom: 'Pause from',
  pauseTo: 'until',
  pauseHint: 'The series carries on afterwards — the rule stays, only these days are dropped.',
  duplicateSeries: 'Duplicate',
  copySuffix: ' (copy)',
  shiftSeries: 'Shift by',
  shiftDays: 'days',
  shiftHint: 'Shifts the whole series. "Every Friday" plus one day becomes "every Saturday".',
  paused: 'Pause recorded.',
  duplicated: 'Copy created — as a draft.',
  shifted: 'Series shifted.',
  actionFailed: 'That did not work.',
  shiftMonthOverflow: 'Not that far — the series would slip into another month.',
  shiftNotShiftable: 'A series on the "first Friday of the month" cannot be shifted by days. Change the rule instead.',

  artwork: 'Artwork',
  addImage: 'Add image',
  dropImages: 'Drop images here, or click',
  imageRotates: 'rotates automatically',
  imageUnnamed: 'no description',
  imageAlt: 'Image description',
  removeImage: 'Remove',
  remove: 'remove',
  fieldTags: 'Tags',
  tagPlaceholder: 'Type and pick from the list',
  tagHint: 'Up to five. Take existing tags where they fit — new ones only when none does.',
  tagLimitReached: 'Five tags is the maximum.',
  tagExisting: 'existing',
  tagCreate: 'create new',
  altLabel: 'Image description',
  altPlaceholder: 'What does the flyer say?',
  uploading: 'Uploading …',
  uploadFailed: 'That image could not be processed.',
  rotationHint: 'Several images take turns across the dates. An image pinned to a date wins.',
  imagesStripped: 'Images are re-encoded on upload — location data from the photo disappears with it.',

  editOccurrence: 'This date only',
  fieldTitleOverride: 'Different title',
  fieldDescriptionOverride: 'Different description',
  fieldPinImage: 'Image for this date',
  overrideHint: 'Leaving it empty means: take what the series says.',

  switchAccount: 'Switch account',

  usersLede: 'Find and open one account on purpose: ban, confirm a claim, answer an access request, delete. Ranks are not handed out here.',
  usersSearch: 'E-mail or name',
  usersEmpty: 'Nobody found.',
  usersBanned: 'Banned',
  usersBan: 'Ban',
  usersUnban: 'Lift ban',
  usersBanReason: 'Reason (appears in the ban notice)',
  usersSince: 'Joined',

  designNeedsClaim: 'Designing works once somebody from the institution has claimed the account. Until then the page keeps the neutral look — we do not publish a visual identity the institution has never seen.',
  designClaimLink: 'Claim this account',

  membershipSearch: 'Find an institution',
  membershipRole: 'Role',
  membershipReason: 'Reasons (go into the log)',
  membershipNonePicked: 'No institution picked yet.',
  membershipPicked: (name) => `Picked: ${name}`,
  membershipHint: 'The normal way is an invitation: the person gets a mail and accepts. A membership is the right to publish under somebody else’s name, so their agreement belongs inside the operation rather than behind it.',
  membershipInvite: 'Send invitation',
  membershipInvited: 'Invitation sent.',
  membershipAssign: 'Add without an invitation',
  membershipAssignHint: 'Admin only, and only where an invitation cannot work — the owner has left, the address bounces, onboarding over the phone. Logged as its own action, so the exception stays readable as one.',
  membershipAssignConfirm: (name) => `Add to "${name}" without their consent? They will be able to publish under that name without having been asked.`,
  membershipAssigned: 'Added.',
  membershipNoAddress: 'This account has no address — inviting works only after a first sign-in.',
  membershipRemove: 'Remove',
  membershipRemoveReason: 'Why is this membership being removed? (goes into the log)',
  membershipRemoved: 'Membership removed.',

  usersRank: 'Rank',
  usersRankHint: 'Only an admin grants moderation. Moderators hand out no ranks themselves — otherwise one careless account is enough to open the door for everyone. Admin is not granted here: that needs access to the server, which is also where the very first one comes from.',
  usersRankGrant: 'Make a moderator',
  usersRankRevoke: 'Remove moderation',
  usersRankReason: 'Reasons (go into the log)',
  usersRankGrantConfirm: (name) => `Make "${name}" a moderator? They will see reports and bans and — on deliberately opening one account — saved dates and followed accounts too.`,
  usersRankRevokeConfirm: (name) => `Remove moderation from "${name}"? Open sessions stop being valid immediately.`,
  usersRankAdminCli: 'Changeable only on the server (npm run grant).',

  adminPasskeys: 'Security keys',
  adminPasskeysNone: 'No key registered — this account is in the enrolment window.',
  adminPasskeyRevoke: 'Revoke',
  adminPasskeyReason: 'Why? (goes into the log)',
  adminPasskeyRevoked: 'Key revoked. Open sessions are invalid.',
  adminPasskeyHint: 'Revoking invalidates live sessions. If it was the last key the enrolment window reopens — the person signs in with a code and registers a new one.',

  usersSearchFirst: 'Type a name or an e-mail address. There is deliberately no list to browse here — an account is opened on purpose.',
  usersVerified: 'Claimed',
  usersUnverified: 'Unclaimed',
  usersVerification: 'Claim',
  usersVerify: 'Mark as claimed',
  usersVerifyReason: 'How do you know? (goes into the log)',
  usersVerifyHint: 'Claimed means somebody from this institution has taken the account over. It unlocks the short address at the root and lets the institution design its calendar page. Ranks are not handed out here — anyone who can promote somebody to moderator from a dashboard has built a way upwards.',
  usersVerifyIrreversible: 'It cannot be taken back here: the short address is by then in Instagram profiles, and removing it turns those into dead links. For an account causing trouble, the ban is the tool.',

  usersExport: 'Download all data',
  usersExportPurpose: 'What for? (goes into the log)',
  usersExportHint: 'Art. 15 access — the same file the person gets themselves under Settings. For somebody who cannot sign in and therefore wrote in. One account at a time, always logged.',

  usersOpen: 'Open account',
  usersBack: 'Back to search',
  usersDetail: 'Account',
  usersLastSeen: 'Last seen',
  usersNever: 'never',
  usersMemberships: 'Belongs to',
  usersNoMemberships: 'No memberships.',
  usersReportsFiled: (n) => `${n} ${n === 1 ? 'report' : 'reports'} filed`,
  usersReportsReceived: (n) => `${n} ${n === 1 ? 'report' : 'reports'} received`,

  activityHeading: 'Saved dates and followed accounts',
  activityPurpose: 'For investigating a report, and nothing else. What follows is a profile of interests and is shown nowhere else in the product — not even to the venue.',
  activityLogged: 'Opening this is logged, with your name and the reason you give.',
  activityReason: 'What for? (goes into the log)',
  activityOpen: 'Show activity',
  activityEmpty: 'Nothing recorded.',
  activityVotes: 'Saved',
  activityFollows: 'Follows',
  activityFailed: 'Could not be loaded.',

  usersDelete: 'Delete account',
  usersDeleteHint: 'Deletion is for a substantiated Art. 17 request, not for annoyance. The everyday tool is the ban: reversible, and it unpublishes rather than destroys.',
  usersDeleteReason: 'Reason (goes into the log, not into the data)',
  usersDeleteConfirm: (name) => `Delete "${name}" for good? Saved dates, followed accounts and sessions go with it. Past dates stay, and the log keeps an identifier only.`,
  usersDeleted: 'Deleted.',

  // The crawl dashboard. Three views of one pipeline: what came in, which
  // sources run, and what waits for a person.
  crawlTitle: 'Crawl',
  crawlLede: 'What the crawler brought in, which sources run, and what is waiting to be approved.',
  crawlTabOverview: 'Overview',
  crawlTabSources: 'Sources',
  crawlTabQueue: 'Queue',
  crawlStart: 'Start a crawl',
  crawlStartHint: 'Queues a run. It starts within seconds and takes a few minutes.',
  crawlRunning: (since) => `Running since ${since}`,
  crawlQueued: 'Queued — starting shortly',
  crawlOff: 'The crawler is switched off.',
  crawlOffHint: 'CRAWL is set to off. That is the kill switch, and it covers this button too.',
  crawlPending: (n) => `${n} waiting`,
  crawlPromotable: (n) => `${n} ready to approve`,
  crawlNoTimeCount: (n) => `${n} with no start time`,
  crawlNoVenueCount: (n) => `${n} with no institution`,
  crawlProblems: 'Problems',
  crawlNoProblems: 'No source needs attention right now.',
  crawlHealthOk: 'Delivering',
  crawlHealthStalled: 'Used to deliver, now nothing',
  crawlHealthNever: 'Has never delivered anything',
  crawlHealthDisabled: 'Switched off',
  crawlUnchecked: 'robots/TDM not recorded',
  crawlUncheckedCount: (n, total) => `${n} of ${total} sources with no recorded robots/TDM state`,
  crawlUncheckedHint: 'With no recorded state a source counts as unchecked, not as permitted.',
  crawlYield: (last, total) => `last ${last} · ${total} in total`,
  crawlRuns: 'Recent runs',
  crawlStaged: (n) => `${n} new`,
  crawlDisable: 'Switch off',
  crawlEnable: 'Switch back on',
  crawlDisableReason: 'Reason for switching it off',
  crawlSources: (n) => `${n} sources`,
  crawlNoTime: 'No start time — the source states none, and a time is never invented.',
  crawlNeedsVenue: 'This source has no institution yet. Attach one before approving:',
  crawlNeedsClassification: 'This source carries no facet hint — set facets or tags here.',
  crawlVenueCommand: (key) => `npm run crawl:review -- venue ${key} <slug> <Name>`,
  crawlApprove: (n) => `Approve ${n}`,
  crawlReject: 'Reject',
  crawlRejectReason: 'Reason for rejecting',
  crawlSelectAll: 'Select all',
  crawlSelectBlocked: 'Include blocked',
  crawlNothingSelected: 'Nothing selected.',
  crawlOpenSource: 'Open the source',
  crawlYearGuessed: 'Year inferred',
  crawlNoStartTime: 'NO TIME',
  crawlRoomHint: (room) => `Room? ${room}`,
  crawlRoom: (room) => `Room: ${room}`,
  crawlPriceFree: 'Free entry',
  crawlPrice: (price) => `Price: ${price}`,
  crawlTicket: (host) => `Ticket: ${host}`,
  crawlFacets: 'Facets (kind:slug, comma separated)',
  crawlTags: 'Tags (comma separated)',
  crawlApproved: (n) => `${n} approved`,
  crawlRefusedCount: (n) => `${n} rejected`,
  crawlEmptyQueue: 'Nothing is waiting to be approved.',

  stats: 'Stats',
  statsLede: 'What arrives, what becomes of it, and what the database already knew.',
  statsOff: 'No analytics configured.',
  statsOffHint: 'ANALYTICS is off. Locally that is the intended state — the numbers arrive with the server.',
  health: 'Health',
  healthMail: 'Mail',
  healthMailHint: 'Login codes are the only way in. A delivery outage locks out everyone.',
  healthWindow: 'Occurrence window',
  healthWindowHint: 'How far ahead dates actually exist. If the job stalls the feed thins from the front with nothing turning red.',
  healthJobs: 'Last runs',
  healthNever: 'never run',
  healthOk: 'running',
  healthStale: 'stalled',
  healthDays: (n) => `${n} ${n === 1 ? 'day' : 'days'}`,

  statsRangeLabel: 'Date range',
  statsRange24h: 'Last 24 hours',
  statsRange7d: 'Last 7 days',
  statsRange30d: 'Last 30 days',
  statsRange90d: 'Last 90 days',
  statsRange365d: 'Last 365 days',
  statsPageviews: 'Page views',
  statsVisitors: 'Visits',
  statsUnreachable: 'Statistics are not answering right now. The site runs regardless — it never waits on them.',
  statsNoToken: 'No read access configured. The numbers are fetched by the server, never by the browser — and the token for that is missing here.',
  statsFailed: 'The request failed — not analytics switched off, but an expired session or a server error.',
  statsAggregate: 'Totals only. There is nothing else to show: IP addresses are hashed and discarded.',

  statsTileVisitors: 'Visitors',
  statsTileCalendarAdds: 'Calendar adds',
  statsTileTicketClicks: 'Ticket clicks',
  statsTraffic: 'Traffic over time',
  statsFunnel: 'Funnel',
  statsVenues: 'Calendar-page views by venue',
  statsNoData: 'No data yet.',

  statsInventory: 'Inventory',
  statsInventoryFailed: 'Inventory failed to load — not an empty database, but a server error.',
  statsPublishing: 'Publishing',
  statsIdle: 'Idle',
  statsPublished7d: 'Newly published (7 days)',
  statsNext7: 'Next 7 days',
  statsNext30: 'Next 30 days',
  statsByCity: 'By city (30 days)',
  statsIncomplete: 'Incomplete series',
  statsNoImage: 'No image',
  statsNoTag: 'No tag',
  statsNoAddress: 'No address',

  statsCanary: 'Login codes',
  statsCanaryHint: 'A gap between the two numbers means mail is not arriving. The login code is the only way in.',
  statsCanaryCount: (consumed, issued) => `${consumed} of ${issued} codes redeemed`,

  healthDatabase: 'Database',
  healthDatabaseHint: 'A write-ahead log that only grows means checkpointing has stopped — that shows up as disk pressure long before anything throws an error.',
  healthWal: 'of which WAL',
  healthSchema: 'Schema',
  healthSchemaApplied: (n) => `${n} ${n === 1 ? 'migration' : 'migrations'} applied`,
  healthBackup: 'Last backup',
  healthDrill: 'Last restore drill',
  healthDrillHint: 'A backup nobody has restored is a hypothesis.',
  healthRateLimit: 'Rate-limit trips',
  healthRateLimitHint: 'The cheapest abuse signal there is: a full window means somebody hit a wall.',
  healthBytes: (mb) => `${mb} MB`,

  navCollaborations: 'Involved',
  collaborationsLede: 'Events somebody has named you on. They appear on your page once you accept.',
  collaborationsEmpty: 'Nobody has named you yet.',
  collaborationAccept: 'Accept',
  collaborationDecline: 'Decline',
  collaborationLeave: 'Take off my page',
  collaborationAccepted: 'Accepted',
  collaborationDeclined: 'Declined',
  collaborationPending: 'Open',
  collaborationBy: (host) => `by ${host}`,
  fieldCollaborators: 'Involved',
  collaboratorsHint: 'Whoever you name here is asked. The event reaches their page only after they accept.',
  collaboratorAdd: 'Invite',
  collaboratorRemove: 'Withdraw invitation',
  collaboratorSearch: 'Name of the person or institution',

  ownProgramme: 'My programme',
  ownProgrammeLede: 'What you are putting on yourself.',
  ownProgrammeEmpty: 'You have not added anything of your own yet.',
  navInstitutions: 'Institutions',
  backToPanel: 'Back to the overview',
  fieldOwner: 'Who is hosting',
  ownerHint: 'Decides whose page the event appears on. Not changeable afterwards.',

  institutionsTitle: 'Institutions',
  institutionsLede: 'For clubs, bars, galleries, collectives — anybody running a programme rather than the odd night. An institution has a calendar page of its own, a team and an export, and a short address once we have confirmed you belong to it.',

  deleteSeries: 'Delete event',
  deleteSeriesConfirm: (title, upcoming) => (upcoming === 1
    ? `Delete “${title}”? One upcoming date leaves your page with it.`
    : `Delete “${title}”? ${upcoming} upcoming dates leave your page with it.`),
  deleteSeriesHint: 'Leaves your page and the feed. Past dates stay in the export — deleting throws nothing away.',
  deletedSeries: 'Deleted.',

  moderation: 'Moderation',
  moderationLede: 'Applications, reports, and what the jobs did last.',
  moderatorOnly: 'This page is for moderators.',
  moderatorNeedsPasskey: 'Moderation stays locked until a security key is registered. Do that under Settings.',
  applications: 'Applications',
  reports: 'Reports',
  nothingPending: 'Nothing pending.',
  reviewNote: 'Note (optional)',
  approve: 'Approve',
  reject: 'Reject',
  approved: 'Approved:',
  rejected: 'Rejected.',
  confirmReject: 'Really reject this application?',
  markReviewed: 'Done',
  reportResolved: 'Report closed.',
  anonymous: 'anonymous',

  reportAge: (h) => (h < 48 ? `${h} h ago` : `${Math.floor(h / 24)} days ago`),
  reportDue: (date) => `Due: ${date}`,
  reportOverdue: 'Past the promised window',
  reportDeadlineErasure: 'Erasure request',
  reportContactBack: (contact) => `Answer to: ${contact}`,
  reportNoContact: 'No reply address given.',
  reportReasonGiven: 'Reasons (required, goes into the log)',
  reportUnpublish: 'Take out of publication',
  reportBan: 'Ban the account',
  reportDismiss: 'Nothing to do',
  reportNeedsReason: 'Not without reasons — "nothing to do" needs them too.',
  reportNoAccount: 'There is no account behind this to ban.',
  reportUnpublished: (n) => (n === 1
    ? 'Done. One listing has been taken out of publication.'
    : `Done. ${n} listings have been taken out of publication.`),
  reportTargetHint: 'The series is taken down, not the single date — "cancelled" would be a statement about the event and would land in every subscribed calendar.',
  jobRuns: 'Recent job runs',
  noJobRuns: 'No run recorded yet.',
  auditLog: 'Log',

  team: 'Team',
  teamHint: 'Whoever is listed here can maintain the programme. Invite people one by one rather than sharing a password — that can be taken back when somebody leaves.',
  roleOwner: 'Owner',
  roleEditor: 'Editor',
  sendInvite: 'Invite',
  inviteFailed: 'The invitation could not be sent.',
  invitePending: 'invited',
  revokeInvite: 'Revoke',
  removeMember: 'Remove',
  confirmRemoveMember: 'Really remove this person?',
  lastOwner: 'The last owner cannot be removed.',
  close: 'Close',
  discardChanges: 'Discard unsaved changes?',
  exportData: 'Export data',
  exportHint: 'Everything you have entered, as one file. No questions, any time.',

  pageBuilder: 'Your page',
  theme: 'Brightness',
  themeDark: 'dark',
  themeLight: 'light',
  accent: 'Accent colour',
  defaultView: 'Starting view',
  howFarAhead: 'How far ahead',
  month: 'month',
  months: 'months',
  whichSeries: 'Which series',
  preview: 'Preview',
  copyLink: 'Copy link',
  copied: 'Copied',
  copyFailed: 'Please copy by hand',

  stepWhat: 'What it is',
  stepWhen: 'When',
  stepFound: 'How it is found',
  stepSeries: 'Series',
  stepOf: (n, total) => `Step ${n} of ${total}`,
  back: 'Back',
  next: 'Next',
  saveDraft: 'Save draft',
  draftSaved: 'Draft saved.',
  useFullForm: 'Everything on one page',
  useWizard: 'Step by step',
  repeatsToggle: 'Repeats',
  flyerQueued: 'Will upload as soon as the draft is saved.',
  collaboratorsLater: 'Inviting collaborators comes later — until then guests belong in the description.',

  publishMissing: (fields) => `Still missing before this can go live: ${LIST.format(fields)}.`,
  // The same sentence, built from parts: each missing piece is a link to the
  // station that asks for it, and the punctuation belongs to the template.
  stillMissing: (parts) => ['Still missing: ', ...parts, '.'],
  missingTitle: 'a title',
  missingPlace: 'an address',
  missingDate: 'a date',
  missingSetting: 'indoor or outdoor',
  missingClassification: 'a tag or a facet',

  fieldPlace: 'Address',
  placeElsewhere: 'Happens somewhere else',
  placePlaceholder: 'Search for an address',
  placeHint: 'Pick from the address book. Creating a new address is a deliberate step — otherwise there are eight spellings of the same one within a year.',
  placeExisting: 'existing',
  placeCreate: 'create new',
  placeNewHeading: 'Add a new address',
  fieldPlaceName: 'Name of the address',
  placeCreateAction: 'Add address',
  placeNameRequired: 'An address needs a name.',
  placeCreateFailed: 'The address could not be created.',

  // ---------------------------------------------------------- /intern/datenfluss
  flowTitle: 'How tba.tips fits together',
  flowLede: 'Fifteen things and what they do to each other. Content arrives from the left, people from the right, and the institution in the middle is what holds the two together.',
  flowHidden: 'This page is linked from nowhere and closed to search engines.',
  flowBoardDesc: 'A diagram of fifteen labelled boxes joined by arrows. The same information is written out below it.',
  flowLegendHuman: 'A step where a person decides',
  flowLegendHint: 'Tap a box to highlight what hangs off it. Tap again to clear.',
  flowLegendReset: 'Clear the highlight',
  flowDetailsTitle: 'In detail',

  flowNodeSource: 'The institution’s website',
  flowNodeSourceNote: 'HTML, RSS or ICS — whatever the institution publishes anyway. Every source is listed with its address and its reading pattern.',
  flowNodeCrawler: 'Crawler',
  flowNodeCrawlerNote: 'Asks robots.txt, honours TDM opt-outs, fetches only when the ETag changed. Takes facts — title, date, time, place. No prose from web pages, no images.',
  flowNodeStaging: 'Staging',
  flowNodeStagingNote: 'None of it is public. An entry waits here until a person looks at it — which is why the arrow out of it starts at moderation and not at the crawler.',
  flowNodeSeries: 'Event',
  flowNodeSeriesNote: 'One row carrying the rule — for a crawled entry the rule is “once”. This is the order that surprises people: a date does not grow into a series, every event is the rule from the start, and a single evening is a rule with exactly one date in it. The other way round a date would have no origin, and turning one into a repeat would mean turning it into something else. The institution owns it, tipsBOT wrote it down — two separate fields, and the card shows both.',
  flowNodeDates: 'Individual dates',
  flowNodeDatesNote: 'Generated nightly from the rule, 18 months ahead. Hand-work — cancellations, moves, overrides — survives every rebuild.',
  flowNodeBot: 'tipsBOT',
  flowNodeBotNote: 'An institution itself, with a page of its own. It owns no event, it wrote one down — and it stays on as an editor after a handover, because the maintained programme is why the institution found us.',
  flowNodeInstitution: 'Institution',
  flowNodeInstitutionNote: 'A row with no e-mail: an account nobody can sign in as. That is what makes it an institution — not a rank, not a checkbox.',
  flowNodePlace: 'Address',
  flowNodePlaceNote: 'Its own entry in the address book. The account is the identity, the address is the place — they coincide on day one and part at the first event held elsewhere.',
  flowNodeMembership: 'Membership',
  flowNodeMembershipNote: 'The only thing that opens the workspace. Editors maintain the programme; owners additionally the name, the address, the look and the members. Three people run one institution — the alternative is a shared login nobody can revoke.',
  flowNodePage: 'Public page',
  flowNodePageNote: 'Unclaimed it sits at /i/name, verified it moves into the root at /name, and the old address redirects permanently. The short name is earned, never assigned.',
  flowNodeVisitor: 'A visitor',
  flowNodeVisitorNote: 'No account, no row. A page view creates nothing — otherwise every crawler would leave a trace.',
  flowNodeGuest: 'Guest',
  flowNodeGuestNote: 'An anonymous row appears on the first write, never on a read.',
  flowNodePerson: 'Person',
  flowNodePersonNote: 'The same row gains a field rather than being replaced. That is why there is no merge path — and why no save can be lost or duplicated.',
  flowNodeModeration: 'Moderation',
  flowNodeModerationNote: 'Releases crawled entries, verifies institutions, handles reports. Every one of those writes a line to the log.',
  flowNodeAdmin: 'Administration',
  flowNodeAdminNote: 'The only rank that grants ranks, and only between person and moderator. Somebody who could hand out moderation while being a moderator could hand it to themselves.',

  flowEdgeReads: 'reads',
  flowEdgeStages: 'stages',
  flowEdgeReleases: 'releases',
  flowEdgeBecomes: 'becomes',
  flowEdgeGenerates: 'generates',
  flowEdgeAuthors: 'wrote it down',
  flowEdgeOwns: 'owns',
  flowEdgeRuns: 'runs',
  flowEdgeAppearsAs: 'appears as',
  flowEdgeOpens: 'opens',
  flowEdgeUntilClaimed: 'until claimed',
  flowEdgeAccepts: 'accepts an invitation',
  flowEdgeApplies: 'applies',
  flowEdgeConfirms: 'confirms',
  flowEdgeGrantsRank: 'grants the rank',
  flowEdgeFirstWrite: 'first save',
  flowEdgeAddsEmail: 'adds an e-mail',
  flowEdgeSaves: 'saves',

  flowGatesTitle: 'Two gates that get confused',
  flowGateMembership: 'Membership decides what somebody may touch — and it has nothing to do with rank. A person holding no rank above person can be the owner of an institution.',
  flowGateVerified: 'Verification decides whether the design surface is open at all. An unclaimed institution keeps the neutral look — on the way out as well as on the way in.',
  flowRanksTitle: 'A rank is a ladder and nothing else',
  flowRanksNote: 'What opens an institution is membership, not rank. Rank carried account type as well until the split — which is why approving an application briefly turned the applicant themselves into an institution.',
  flowKindOutside: 'outside',
  flowKindData: 'data',
  flowKindInstitution: 'institution',
  flowKindPerson: 'personal account',
  flowKindGrant: 'permission',
  flowKindRank: 'rank',
  flowKindsTitle: 'The kinds',
  flowAdminHint: 'How content, institutions and people fit together. Unlisted and closed to search engines — reachable from here so the page does not get lost.',
  badgeBot: 'BOT',
  badgeBotTitle: 'Programme maintained by tipsBOT, not by the institution itself',
  adminOnly: 'visible to administrators only',
  badgeAutomatic: 'automatic',
};
